import React, { useEffect } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { parse } from "query-string";

import I18n from "../../I18n";

import { mapProperties, hydrateRoute } from "colbi_web_ui/lib/utils";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    getColbiDdEntities,
    getTranslationTableDatasetLocaleAndEntities,
    deleteTranslationTableAction,
    downloadAction,
    downloadCsvStreamAction,
} from "../../store/actions";

import { LoadingIndicator, rows, DataGrid } from "colbi_web_ui/lib/components";
import { SovosBox, SovosPageLayout, SovosPageContentGeneric } from "mosaic-react";

import {
    buildSettingsStaticLinks,
    generateSettingsMenuItems,
} from "../../components/GlobalLayout/menuBuilderUtils";
import SettingsCreateButton from "../../components/SettingsCreateButton/SettingsCreateButton";
import SettingsToolbar from "../../components/SettingsToolbar/SettingsToolbar";
import TranslationTableFormButton from "../../components/TranslationTableFormButton/TranslationTableFormButton";

import styles from "./Settings.module.sass";
import { settingsQueries } from "./settingsQueries";
import {
    getSettingsContentSx,
    getSettingsGridModifiersSx,
    getSettingsPageSx,
    resolveSettingsListTotal,
    settingsListFitsViewport,
} from "./settingsGridLayout";

import {
    decodeUserSession,
    redirectNotValid,
    BuildSovosNavProps,
} from "../../utils/utils";

import GlobalLayout from "../../components/GlobalLayout/GlobalLayout";
import { useAuth } from "react-oidc-context";

const getRowTitle = (row = {}, i18n) => {
    if (row.__typename === "TranslationTable") {
        return i18n`Configuration`;
    }

    return (
        row.taxRegistrationNumber ||
        row.name ||
        row.ReportName ||
        row.RuleID ||
        row.KpiID ||
        row.description ||
        row.id ||
        ""
    );
};

const Settings = (props) => {
    const { user, fetch, match, setLocale, location, history, pushModal, reload } = props;

    const locale = match.params.locale || "en";
    const i18n = I18n.use(locale);
    const auth = useAuth();

    const headerTitle =
        match.params.section === "kpis"
            ? `settings_menu_${match?.params?.section || ""}`
            : match.params.section === "translations_table"
                ? "translation_table"
                : `${match?.params?.section || ""}`;

    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);

    const userInformation = decodeUserSession(user);

    const menuItems = generateSettingsMenuItems(userInformation?.role, history, locale, i18n) || [];
    const navigation = menuItems.map((elem) => ({
        ...elem,
        name: I18n.translate`${elem.name}`,
    }));

    const staticLinks = buildSettingsStaticLinks(navigation, history, match.params, i18n);

    const redirect = redirectNotValid(userInformation, locale, true, auth);

    if (redirect) {
        return redirect;
    }

    if (!(userInformation && userInformation.accessSettings)) {
        return <Redirect to={`/${locale}/unauthorized`} />;
    }

    const queryParams = parse(location.search) || {};
    const routeParams = match.params || {};
    const params = {
        ...queryParams,
        ...routeParams,
    };

    const queries = settingsQueries(params);
    const downloadableQueries = queries.filter(({ downloadable }) => downloadable);

    const data = mapProperties(
        {
            id: "results",
            card: rows.CellCard,
            items: "query(0).list",
            header: "query(0).header",
            footer: "query(0).footer",
            metadata: "query(0).metadata",
            status: "status(0)",
            filter: "param(filter)",
            sort: "param(sort)",
            availableFilters: "query(0).filters",
            wrapper: "wrapper",
            total: "query(0).metadata.total",
            pageSize: 20000,
            enablePagination: false,
            toolbar: SettingsToolbar,
            toolbarProps: {
                data: "query(0).list",
            },
        },
        (fetch || {}).results,
        (fetch || {}).status,
        queries,
        props,
        params,
        i18n,
        locale,
    );

    const currentSection = match.params.section || "/";
    const floatCreateTypename = (navigation.find(({ section }) => section === currentSection) || {})
        .createType;

    if (currentSection === "/") {
        const basePath = "/settings/:organizationId?";
        const hydrated = hydrateRoute(basePath, { ...params });
        return <Redirect to={`${hydrated}${navigation[0]?.section || "projects"}`} replace />;
    }

    const navProps = BuildSovosNavProps(
        undefined,
        [],
        { name: "" },
        {},
        userInformation,
        staticLinks || [],
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params,
        staticLinks,
        styles,
    };

    const rowCount = data?.items?.length ?? 0;
    const isLoading = data?.status?.loading ?? false;
    const useAutoHeight = !isLoading && settingsListFitsViewport(rowCount);

    const gridMetadata = {
        ...(data?.metadata || {}),
        total: resolveSettingsListTotal({
            items: data?.items,
            metadata: data?.metadata,
            status: data?.status,
        }),
    };

    const gridKey = `${currentSection}-${params.filter || ""}-${params.sort || ""}`;

    const handleRowClick = (clickParams) => {
        const row = clickParams?.row || {};
        const { id, __typename } = row;
        if (!__typename || !id) {
            return;
        }
        if (__typename === "TranslationTable") {
            history.push(`/${locale}/settings/translations_table/builder/${id}`);
            return;
        }
        pushModal({
            title: getRowTitle(row, i18n),
            view: `${__typename}Form`,
            args: { id },
        })
            .then(() => {
                history.push(match.url);
                reload && reload();
            })
            .catch(() => null);
    };

    return (
        <GlobalLayout {...globalLayoutProps}>
            <SovosPageLayout
                pageTitle={i18n`${headerTitle}`}
                titleBarActions={[
                    currentSection === "translations_table" ? (
                        <TranslationTableFormButton key="translationForm" />
                    ) : (
                        <SettingsCreateButton key="settingsCreate" typename={floatCreateTypename} />
                    ),
                ]}
            >
                <SovosBox sx={getSettingsPageSx(useAutoHeight)}>
                    <SovosPageContentGeneric sx={getSettingsContentSx(useAutoHeight)}>
                        {fetch.activity && (
                            <LoadingIndicator className={`${styles["loading-indicator"]}`} />
                        )}
                        <DataGrid
                            key={gridKey}
                            {...(data || {})}
                            metadata={gridMetadata}
                            autoHeight={useAutoHeight}
                            enablePagination={false}
                            onRowClickCustom={handleRowClick}
                            modifierssx={getSettingsGridModifiersSx(useAutoHeight)}
                        />
                    </SovosPageContentGeneric>
                </SovosBox>
            </SovosPageLayout>
        </GlobalLayout>
    );
};

Settings.queries = settingsQueries;

export default connect(
    ({ user, fetch, menu, uploads }) => ({
        user,
        fetch,
        menu,
        uploads,
    }),
    {
        toggleMenu,
        logout,
        setLocale,
        pushModal,
        popModal,
        removeModal,
        pushDialog,
        uploadFiles: uploadAction,
        submitAuditNote: auditNoteSubmit,
        getColbiDdEntities,
        getTranslationTableDatasetLocaleAndEntities,
        deleteTranslationTableAction,
        download: downloadAction,
        downloadCsvStreamAction,
    },
)(withRouter(Settings));
